import { useMemo } from "react";

import { Box, Button, Checkbox, Grid, Stack, Typography } from "@mui/material";
import { toOptionItems } from "@shared";
import { FormikProps } from "formik";

import { ClientPersonalDataResponse } from "@/api";
import { FormikAutocompleteSelect } from "@/common/components/fields/formik/formik-autocomplete/FormikAutocompleteSelect";
import { NotImplementedWrapper } from "@/common/components/not-implemented-wrapper/NotImplementedWrapper";
import { useDropdownOptions } from "@/common/hooks/useDropdownOptions";
import { EditClientType } from "@/pages/clients/client/components/tabs/master-data-tab/views/personal-data/EditClient.types";
import { useAmsAssignment } from "@/pages/clients/client/components/tabs/master-data-tab/views/personal-data/hooks/useAmsAssignment";
import { useTranslation } from "@/translations";

interface Props {
  formik: FormikProps<EditClientType>;
  clientId: string;
  personalData: ClientPersonalDataResponse | undefined;
}

export function PersonalDataEmploymentSection({ formik, clientId, personalData }: Props) {
  const { t } = useTranslation();
  const { employmentStatuses } = useDropdownOptions();
  const employmentStatusOptions = useMemo(() => toOptionItems(employmentStatuses), [employmentStatuses]);

  const { performAssignment, isAssigning, isBooked, isOutcomePending, isDisabled } = useAmsAssignment({
    clientId,
    personalData,
    isFormDirty: formik.dirty,
  });

  return (
    <Grid container rowSpacing={2} columnSpacing={2}>
      <Grid size={12}>
        <FormikAutocompleteSelect
          formik={formik}
          fieldName="employmentStatus"
          label={t("clients.create.employmentStatus")}
          options={employmentStatusOptions}
          required
        />
      </Grid>
      <Grid size={12}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <NotImplementedWrapper>
            <Box display="flex" alignItems={"center"}>
              <Checkbox size="small" />
              <Typography>{t("clients.client.masterData.personalData.amsAssignmentConsented")}</Typography>
            </Box>
          </NotImplementedWrapper>
          <Stack alignItems="flex-end" gap={0.5}>
            <Button variant="outlined" onClick={performAssignment} loading={isAssigning} disabled={isDisabled}>
              {t("clients.client.masterData.personalData.performAmsAssignment")}
            </Button>
            {isBooked && (
              <Typography variant="caption" color="text.secondary">
                {t("clients.client.masterData.personalData.amsAssignment.alreadyBooked")}
              </Typography>
            )}
            {!isBooked && isOutcomePending && (
              <Typography variant="caption" color="text.secondary">
                {t("clients.client.masterData.personalData.amsAssignment.outcomePending")}
              </Typography>
            )}
            {!isBooked && !isOutcomePending && formik.dirty && (
              <Typography variant="caption" color="text.secondary">
                {t("clients.client.masterData.personalData.amsAssignment.saveFirst")}
              </Typography>
            )}
          </Stack>
        </Box>
      </Grid>
    </Grid>
  );
}
